/**
 * Every declared opt-out, by name. The action-ledger test walks this so an
 * exemption is a visible line in a list rather than an absence nobody notices.
 */ const registry = new Map();
export const listNonOptimistic = ()=>[
        ...registry.keys()
    ].sort();
/** The recorded reason for an opt-out — read by the ledger test's audit. */ export const getNonOptimisticReason = (name)=>{
    var _registry_get;
    return (_registry_get = registry.get(name)) === null || _registry_get === void 0 ? void 0 : _registry_get.because;
};
/** Test-only: forget every declaration (module registries outlive a suite). */ export const __resetNonOptimisticRegistry = ()=>{
    registry.clear();
};
/**
 * Declare a write that deliberately is NOT optimistic.
 *
 * `defineMutation` makes the optimistic and cross-tab contracts impossible to
 * omit, which is right for the writes that can honour them — and wrong as a
 * blanket rule, because some genuinely cannot:
 *
 * - The server invents the ids (`duplicateTask` creates N tasks; the client
 *   cannot predict a single one of them).
 * - The server resolves the affected set (a filter-scoped bulk delete spans
 *   pages this tab never loaded, so there is no local population to patch).
 * - The mutated thing is not a collection entity at all (an attachment).
 *
 * For those, the honest answer is "no overlay", and the failure mode this
 * function exists to prevent is that answer being given *silently* — by writing
 * a plain `await API(...); push(...)` wrapper that reads exactly like a mutation
 * nobody got round to making optimistic. There is no way to tell the two apart
 * after the fact, so the ledger cannot audit them and they accumulate.
 *
 * So the opt-out is a declaration too. `because` is required and is the whole
 * point: it turns "this isn't optimistic" from an omission into a claim with an
 * author, which review can push back on and the ledger can list.
 *
 * ```ts
 * export const duplicateTask = nonOptimistic({
 *   name: 'task.duplicate',
 *   because:
 *     'the server creates N tasks with ids the client cannot predict; the ' +
 *     'result set arrives via the WS refetch',
 *   run: (...args: Parameters<typeof TaskAPI.duplicateTask>) =>
 *     TaskAPI.duplicateTask(...args),
 * });
 * ```
 *
 * The returned value is callable exactly like the raw function it wraps, so
 * adopting it at an existing call site is a no-op for callers.
 */ export function nonOptimistic(def) {
    const { name, because, run } = def;
    if (!(because === null || because === void 0 ? void 0 : because.trim())) {
        // A blank reason is the exact failure this function exists to stop: the
        // shape of a declaration with none of its content.
        throw new Error(`nonOptimistic(${name}): 'because' must explain why no overlay is ` + `possible. If the answer is "nobody has done it yet", the write ` + `belongs in defineMutation instead.`);
    }
    const callable = (...args)=>run(...args);
    // Non-enumerable so the wrapper still structurally matches the bare function
    // it replaced — spreads and `{ ...api }` re-exports do not pick up metadata
    // the original never had.
    Object.defineProperties(callable, {
        name: {
            value: name,
            configurable: true
        },
        because: {
            value: because,
            configurable: true
        },
        isNonOptimistic: {
            value: true,
            configurable: true
        }
    });
    registry.set(name, callable);
    return callable;
}
